import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, IconButton, CircularProgress,
  TextField, MenuItem, Stack, ToggleButton, ToggleButtonGroup, Button
} from '@mui/material';
import { ArrowBack, Home, Print, Description, GridOn } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { unzipSync, zipSync, strToU8, strFromU8 } from 'fflate';
import TimetableNav from './TimetableNav';
import { LEARNER_PATHWAYS } from '../constants/pathways';
import useCompanyLogo from '../hooks/useCompanyLogo';

const API = process.env.REACT_APP_API_URL;

// Print styles: print ONLY the timetable sheets (not the app shell / nav / controls),
// one timetable per page, landscape so wide grids fit. Sparse teacher timetables
// additionally squeeze their empty period columns into thin strips.
const printCss = () => `
@media print {
  @page { size: landscape; margin: 8mm; }
  /* Hide the whole app, then reveal just the print area. */
  body * { visibility: hidden !important; }
  .tt-print-area, .tt-print-area * { visibility: visible !important; }
  .tt-print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; }
  .no-print { display: none !important; }
  .tt-sheet { page-break-after: always; box-shadow: none !important; border: none !important; margin: 0 0 6mm 0 !important; }
  .tt-sheet:last-child { page-break-after: auto; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Keep each grid within the landscape page so it prints on one page wide with
     no manual scaling: full-width fixed layout + tighter cells. */
  .tt-print-area .tt-table { width: 100% !important; table-layout: fixed; }
  .tt-print-area .tt-table th, .tt-print-area .tt-table td { padding: 2px 3px !important; font-size: 10px !important; word-break: break-word; }
  .tt-print-area .tt-cell-meta { font-size: 9px !important; }
}
.tt-table { border-collapse: collapse; width: 100%; table-layout: auto; }
.tt-table th, .tt-table td { border: 1px solid #999; padding: 4px 6px; text-align: center; font-size: 12px; vertical-align: middle; }
.tt-table th { background: #f0f0f0; }
.tt-day { background: #fafafa; font-weight: 700; }
.tt-cell-sub { font-weight: 700; display: block; }
.tt-cell-meta { color: #555; font-size: 11px; display: block; }
.tt-nonformal td { background: #f0f0f0; font-size: 11px; text-align: left; }
.tt-nonformal .tt-day { text-align: center; }
.tt-fixed { background: #eef3fb; }
.tt-fixed .tt-cell-sub { font-style: italic; }
.tt-brk { background: #ececec; width: 22px; padding: 2px !important; }
.tt-brk-label { writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; font-size: 10px; font-weight: 700; color: #555; margin: 0 auto; letter-spacing: .5px; }
.tt-brk-h { background: #ececec; width: 22px; padding: 2px !important; }
/* Squeezed empty columns (teacher view): thin, with the time shown vertically. */
.tt-empty-col { width: 16px; padding: 2px !important; }
.tt-empty-time { writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; font-size: 9px; font-weight: 400; color: #9a9a9a; margin: 0 auto; }
`;

// Non-formal / co-curricular programmes run after the last lesson (KICD guideline:
// "...plot specified non-formal programmes after class activities from 3.20 to 4.45 pm").
const NONFORMAL_END = '16:45';
const NONFORMAL_ACTIVITIES = 'Games · Clubs & Societies · Guidance & Counselling · Community Service Learning';

const TimetablePrint = () => {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [years, setYears] = useState([]);
  const [year, setYear]   = useState('');
  const [term, setTerm]   = useState(1);
  const [view, setView]   = useState('class');   // 'class' | 'teacher' | 'master'
  const [scope, setScope] = useState('all');       // 'all' | a classCode / employeeNo
  const [pathway, setPathway] = useState('');      // '' = whole class (option blocks stacked); else STEM/SS/… view

  const [config, setConfig] = useState(null);
  const [classes, setClasses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [entries, setEntries]   = useState([]);
  const [subjPathway, setSubjPathway] = useState({});   // stageCode -> { areaCode -> pathway } (from Stage Subjects)
  const [company, setCompany]   = useState({});
  const logoSrc = useCompanyLogo(company);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const school = JSON.parse(localStorage.getItem('school') || '{}');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/academics/years`, { headers }),
      axios.get(`${API}/api/academics/classes`, { headers }),
      axios.get(`${API}/api/employees`, { headers, params: { department: 'Teaching' } }),
      axios.get(`${API}/api/academics/current-period`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/company`, { headers }).catch(() => ({ data: {} })),
    ]).then(([y, c, t, cp, co]) => {
      setYears(y.data);
      const active = y.data.find(yr => yr.isActive) || y.data[0];
      if (cp.data?.year) setYear(cp.data.year);
      else if (active)   setYear(active.year);
      if (cp.data?.term) setTerm(cp.data.term);
      setClasses(c.data); setTeachers(t.data); setCompany(co.data || {});
    }).catch(err => setError(err.response?.data?.error || 'Failed to load reference data'));
  }, []); // eslint-disable-line

  const teacherName = useCallback((no) => {
    const t = teachers.find(x => x.employeeNo === no);
    return t ? `${t.firstName} ${t.lastName}` : no;
  }, [teachers]);
  const className   = useCallback((code) => classes.find(c => c.classCode === code)?.className || code, [classes]);
  const teacherInitials = useCallback((no) => {
    const t = teachers.find(x => x.employeeNo === no);
    if (!t) return no;
    return (`${(t.firstName || '')[0] || ''}${(t.lastName || '')[0] || ''}`).toUpperCase() || no;
  }, [teachers]);

  const load = useCallback(async () => {
    if (!year || !term) return;
    setLoading(true); setError('');
    try {
      const [c, e, sla] = await Promise.all([
        axios.get(`${API}/api/timetable/config`, { headers, params: { academicYear: year, term } }),
        axios.get(`${API}/api/timetable/entries`, { headers, params: { academicYear: year, term } }),
        axios.get(`${API}/api/academics/stage-learning-areas`, { headers, params: { year } }).catch(() => ({ data: [] })),
      ]);
      setConfig(c.data); setEntries(e.data);
      // Build stageCode -> { areaCode -> pathway } so the per-pathway view can
      // resolve each option block to the subject that pathway takes.
      const map = {};
      (sla.data || []).forEach(s => {
        (map[s.stageCode] = map[s.stageCode] || {})[s.areaCode] = s.pathway || 'Common';
      });
      setSubjPathway(map);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load timetable');
    } finally { setLoading(false); }
  }, [year, term]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setScope('all'); setPathway(''); }, [view]);

  // Resolve an option block's child lessons to what the selected pathway takes.
  // No pathway → all children (stacked); a pathway → only its subject(s), or
  // null when that pathway has no subject in this block (a free/study slot).
  const resolveBlockKids = useCallback((stageCode, kids) => {
    if (!pathway) return kids;
    const m = subjPathway[stageCode] || {};
    return kids.filter(k => (m[k.areaCode] || 'Common') === pathway);
  }, [pathway, subjPathway]);

  const days = config?.daysOfWeek || [];

  // ── Band helpers (per-band bell schedules) ──
  const bandForStage = useCallback((stageCode) => {
    const bands = config?.bands || [];
    const hit = bands.find(b => (b.appliesToStages || []).includes(stageCode));
    if (hit) return hit;
    if (bands.length === 1) return bands[0];
    if ((config?.periods || []).length) return { periods: config.periods };
    return null;
  }, [config]);
  // Full day incl. break/lunch/games strips, in clock order — for the printed grid.
  const slotsFor = useCallback((stageCode) =>
    [...(bandForStage(stageCode)?.periods || [])].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')),
    [bandForStage]);
  // Default strip label for a non-lesson period.
  const breakLabel = (p) => p.label || ({ break: 'Break', lunch: 'Lunch', games: 'Games', assembly: 'Assembly' }[p.type] || p.type);
  // Reserved slot (PPI, assembly…) at a day+period for a given band, if any.
  // A fixed activity with bandCodes applies only to those bands; empty = all.
  const fixedAt = useCallback((day, periodNo, bandCode) =>
    (config?.fixedActivities || []).find(f => f.day === day && f.periodNo === periodNo &&
      (!(f.bandCodes && f.bandCodes.length) || (bandCode && f.bandCodes.includes(bandCode)))),
    [config]);
  // Teachers span bands → merged real-time axis.
  const mergedPeriods = useMemo(() => {
    const all = (config?.bands || []).flatMap(b => (b.periods || []).filter(p => p.type === 'lesson'));
    if ((config?.periods || []).length) all.push(...config.periods.filter(p => p.type === 'lesson'));
    const byStart = {};
    all.forEach(p => { if (p.startTime && !byStart[p.startTime]) byStart[p.startTime] = { periodNo: p.startTime, startTime: p.startTime, endTime: p.endTime, lessonNo: null }; });
    return Object.values(byStart).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }, [config]);
  const stageOfClass = useCallback((code) => classes.find(c => c.classCode === code)?.stageCode, [classes]);

  // Bands for the master view (legacy single schedule → one synthetic band = all classes).
  const bandList = useMemo(() => {
    const bands = config?.bands || [];
    if (bands.length) return bands;
    if ((config?.periods || []).length) return [{ bandCode: 'DEFAULT', name: 'All classes', appliesToStages: null, periods: config.periods }];
    return [];
  }, [config]);
  const classesInBand = useCallback((band) =>
    band.appliesToStages == null ? classes : classes.filter(c => (band.appliesToStages || []).includes(c.stageCode)),
    [classes]);

  // School letterhead — logo + name + address + phone (top of every printed page).
  const Letterhead = () => (
    <Box className="tt-letterhead" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, pb: 0.75, borderBottom: '2px solid #333' }}>
      {logoSrc && (
        <img className="tt-logo" alt=""
          src={logoSrc}
          style={{ height: 46, width: 46, objectFit: 'contain' }} />
      )}
      <Box sx={{ lineHeight: 1.25, flex: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{company.name || school.name || 'School'}</Typography>
        {company.name2 && <Typography sx={{ fontSize: 11 }}>{company.name2}</Typography>}
        <Typography sx={{ fontSize: 10.5, color: '#555' }}>
          {[company.address, company.city, company.county && `${company.county} County`].filter(Boolean).join(', ')}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: '#555' }}>
          {[company.phone && `Tel: ${company.phone}${company.phone2 ? ` / ${company.phone2}` : ''}`, company.email].filter(Boolean).join('   ·   ')}
        </Typography>
      </Box>
    </Box>
  );

  const Footer = ({ page, pages }) => (
    <Box className="tt-footer" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 0.5, borderTop: '1px solid #ccc' }}>
      <Typography sx={{ fontSize: 9.5, color: '#777' }}>EduMIS · Powered by Dataport Technologies</Typography>
      <Typography sx={{ fontSize: 9.5, color: '#777' }}>Page {page} of {pages}</Typography>
    </Box>
  );

  // One printed page = letterhead + body + footer.
  const Page = ({ page, pages, children }) => (
    <Card className="tt-sheet" elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Letterhead />
        {children}
        <Footer page={page} pages={pages} />
      </CardContent>
    </Card>
  );

  // One weekly grid (days × periods). `periods` may include break/lunch/games
  // rows, which render as thin full-height strips between the lesson columns.
  const isStrip = (p) => p.type && p.type !== 'lesson';
  const SheetBody = ({ title, subtitle, periods, resolve, bandCode, squeeze }) => {
    // Non-formal band starts when the last lesson of the day ends.
    const lastLesson = periods.filter(p => !isStrip(p)).reduce((mx, p) => (p.endTime && p.endTime > mx ? p.endTime : mx), '');
    // Teacher timetables are sparse: a lesson column is "empty" when no day carries
    // a lesson or fixed activity in it. When squeezing (teacher view) those columns
    // render as thin strips so the used columns fit portrait paper.
    const used = {};
    if (squeeze) periods.forEach(p => { if (!isStrip(p)) used[p.periodNo] = days.some(day => fixedAt(day, p.periodNo, bandCode) || resolve(day, p)); });
    const isEmptyCol = (p) => squeeze && !isStrip(p) && !used[p.periodNo];
    return (
    <>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary" mb={1}>{subtitle}</Typography>}
      <Box sx={{ overflowX: 'auto' }}>
        <table className="tt-table">
          <thead>
            <tr>
              <th>Day</th>
              {periods.map(p => isStrip(p) ? (
                <th key={p.periodNo} className="tt-brk-h" title={`${breakLabel(p)} ${p.startTime || ''}${p.endTime ? `–${p.endTime}` : ''}`}></th>
              ) : isEmptyCol(p) ? (
                <th key={p.periodNo} className="tt-empty-col" title={`${p.startTime || ''}${p.endTime ? `–${p.endTime}` : ''}`}>
                  <span className="tt-empty-time">{p.startTime || (p.lessonNo ? `P${p.lessonNo}` : '')}</span>
                </th>
              ) : (
                <th key={p.periodNo}>
                  {p.lessonNo ? `P${p.lessonNo}` : (p.startTime || '')}<br />
                  <span style={{ fontWeight: 400, fontSize: 10 }}>{p.startTime}{p.endTime ? `–${p.endTime}` : ''}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((day, di) => (
              <tr key={day}>
                <td className="tt-day">{day}</td>
                {periods.map(p => {
                  if (isStrip(p)) {
                    return di === 0
                      ? <td key={p.periodNo} className="tt-brk" rowSpan={days.length}><div className="tt-brk-label">{breakLabel(p)}</div></td>
                      : null;
                  }
                  if (isEmptyCol(p)) return <td key={p.periodNo} className="tt-empty-col"></td>;
                  const fx = fixedAt(day, p.periodNo, bandCode);
                  if (fx) return <td key={p.periodNo} className="tt-fixed"><span className="tt-cell-sub">{fx.label}</span></td>;
                  const cell = resolve(day, p);
                  return (
                    <td key={p.periodNo}>
                      {cell ? (<>
                        <span className="tt-cell-sub">{cell.top}</span>
                        <span className="tt-cell-meta">{cell.bottom}</span>
                      </>) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {periods.length > 0 && (
            <tfoot>
              <tr className="tt-nonformal">
                <td className="tt-day">{lastLesson && lastLesson < NONFORMAL_END ? `${lastLesson}–${NONFORMAL_END}` : 'After lessons'}</td>
                <td colSpan={periods.length}>
                  <strong>Non-formal Programmes</strong> (Mon–Fri) — {NONFORMAL_ACTIVITIES}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </Box>
    </>
    );
  };

  // Master body for one (band, day): rows = band's classes, cols = band's periods.
  const MasterBody = ({ band, day, bandClasses, bandPeriods }) => (
    <>
      <Typography variant="h6" fontWeight={700}>Master — {band.name || band.bandCode} · {day}</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>{year} · Term {term}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <table className="tt-table">
          <thead>
            <tr>
              <th>Class</th>
              {bandPeriods.map(p => isStrip(p) ? (
                <th key={p.periodNo} className="tt-brk-h" title={`${breakLabel(p)} ${p.startTime || ''}${p.endTime ? `–${p.endTime}` : ''}`}></th>
              ) : (
                <th key={p.periodNo}>
                  {p.lessonNo ? `P${p.lessonNo}` : (p.startTime || '')}<br />
                  <span style={{ fontWeight: 400, fontSize: 10 }}>{p.startTime}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bandClasses.map((c, ci) => (
              <tr key={c.classCode}>
                <td className="tt-day">{c.className || c.classCode}</td>
                {bandPeriods.map(p => {
                  if (isStrip(p)) {
                    return ci === 0
                      ? <td key={p.periodNo} className="tt-brk" rowSpan={bandClasses.length}><div className="tt-brk-label">{breakLabel(p)}</div></td>
                      : null;
                  }
                  const fx = fixedAt(day, p.periodNo, band?.bandCode);
                  if (fx) return <td key={p.periodNo} className="tt-fixed"><span className="tt-cell-sub">{fx.label}</span></td>;
                  const e = entries.find(x => x.type === 'lesson' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(c.classCode));
                  if (e) return (
                    <td key={p.periodNo}>
                      <span className="tt-cell-sub">{e.areaCode}</span>
                      <span className="tt-cell-meta">{teacherInitials(e.employeeNo)}</span>
                    </td>
                  );
                  const res = entries.find(x => x.type === 'optionBlock' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(c.classCode));
                  if (res) {
                    const kids = entries.filter(x => x.type === 'lesson' && x.optionBlockId === res.optionBlockId && x.day === day && x.periodNo === p.periodNo);
                    return (
                      <td key={p.periodNo}>
                        <span className="tt-cell-sub">{kids.map(k => k.areaCode).join(' / ') || 'Block'}</span>
                        <span className="tt-cell-meta">{kids.map(k => teacherInitials(k.employeeNo)).join(' / ')}</span>
                      </td>
                    );
                  }
                  return <td key={p.periodNo}></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </>
  );

  // Flatten all views to a list of page bodies, then wrap each in a numbered Page.
  const renderSheets = () => {
    const pagesArr = [];
    if (view === 'master') {
      bandList.forEach(band => {
        const bandClasses = classesInBand(band);
        // Full day incl. break/lunch/games strips, in clock order.
        const bandPeriods = [...(band.periods || [])].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
        if (bandClasses.length === 0 || bandPeriods.filter(p => p.type === 'lesson').length === 0) return;
        days.forEach(day => pagesArr.push({
          key: `${band.bandCode}-${day}`,
          node: <MasterBody band={band} day={day} bandClasses={bandClasses} bandPeriods={bandPeriods} />,
        }));
      });
    } else if (view === 'class') {
      const list = scope === 'all' ? classes.map(c => c.classCode) : [scope];
      list.forEach(code => pagesArr.push({
        key: code,
        node: <SheetBody title={pathway ? `${className(code)} — ${pathway}` : className(code)}
          subtitle={`${year} · Term ${term} · ${pathway ? `${pathway} pathway` : 'Class'} Timetable`}
          periods={slotsFor(stageOfClass(code))} bandCode={bandForStage(stageOfClass(code))?.bandCode}
          resolve={(day, p) => {
            const core = entries.find(x => x.type === 'lesson' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(code));
            if (core) return { top: core.areaCode, bottom: teacherInitials(core.employeeNo) };
            const res = entries.find(x => x.type === 'optionBlock' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(code));
            if (res) {
              const allKids = entries.filter(x => x.type === 'lesson' && x.optionBlockId === res.optionBlockId && x.day === day && x.periodNo === p.periodNo);
              const kids = resolveBlockKids(stageOfClass(code), allKids);
              if (!kids.length) return null;   // this pathway is free this slot
              return { top: kids.map(k => k.areaCode).join(' / ') || 'Option block', bottom: kids.map(k => teacherInitials(k.employeeNo)).join(' / ') || (pathway ? '' : 'choose one') };
            }
            return null;
          }} />,
      }));
    } else {
      const list = scope === 'all' ? teachers.map(t => t.employeeNo) : [scope];
      list.forEach(no => pagesArr.push({
        key: no,
        node: <SheetBody title={teacherName(no)} subtitle={`${year} · Term ${term} · Teacher Timetable`}
          periods={mergedPeriods} squeeze
          resolve={(day, p) => {
            const e = entries.find(x => x.employeeNo === no && x.day === day && x.startTime === p.startTime && x.type === 'lesson');
            return e ? { top: e.areaCode, bottom: (e.classCodes || []).length ? e.classCodes.join(', ') : `${e.cohortCode || ''} elective` } : null;
          }} />,
      }));
    }
    const total = pagesArr.length;
    return pagesArr.map((pg, i) => <Page key={pg.key} page={i + 1} pages={total}>{pg.node}</Page>);
  };

  // ── Export (editable Word & Excel) ───────────────────────────────────────────
  // Build the current view/scope as plain data grids so the same shape feeds both
  // an .xlsx workbook and an editable Word (.doc) HTML table.
  const cellFor = (top, bottom) => (top ? (bottom ? `${top} (${bottom})` : top) : '');
  const periodHead = (p) => `${p.lessonNo ? `P${p.lessonNo}` : (p.startTime || '')}${p.startTime ? `\n${p.startTime}${p.endTime ? `–${p.endTime}` : ''}` : ''}`;
  const nonformalRow = (cols) => ['Non-formal', `Games · Clubs & Societies · Guidance & Counselling · Community Service Learning`, ...Array(Math.max(0, cols - 1)).fill('')];

  // A sheet = { name, title, header:[], rows:[[]], brk:[bool] } where brk marks
  // which columns are break/lunch/games strips (index 0 is the Day/Class column).
  const cellForClass = (day, p, code, bandCode) => {
    const fx = fixedAt(day, p.periodNo, bandCode); if (fx) return fx.label;
    const core = entries.find(x => x.type === 'lesson' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(code));
    if (core) return cellFor(core.areaCode, teacherInitials(core.employeeNo));
    const res = entries.find(x => x.type === 'optionBlock' && x.day === day && x.periodNo === p.periodNo && (x.classCodes || []).includes(code));
    if (res) {
      const allKids = entries.filter(x => x.type === 'lesson' && x.optionBlockId === res.optionBlockId && x.day === day && x.periodNo === p.periodNo);
      const kids = resolveBlockKids(stageOfClass(code), allKids);
      if (!kids.length) return '';
      return cellFor(kids.map(k => k.areaCode).join('/'), kids.map(k => teacherInitials(k.employeeNo)).join('/')); }
    return '';
  };

  const buildSheets = () => {
    const sheets = [];
    if (view === 'master') {
      bandList.forEach(band => {
        const bandClasses = classesInBand(band);
        const periods = [...(band.periods || [])].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
        if (!bandClasses.length || !periods.filter(p => !isStrip(p)).length) return;
        const brk = [false, ...periods.map(isStrip)];
        days.forEach(day => {
          const header = ['Class', ...periods.map(p => isStrip(p) ? breakLabel(p) : periodHead(p))];
          const rows = bandClasses.map(c => [c.className || c.classCode, ...periods.map(p => isStrip(p) ? '' : cellForClass(day, p, c.classCode, band?.bandCode))]);
          sheets.push({ name: `${band.bandCode || band.name}-${day}`.slice(0, 31), title: `Master — ${band.name || band.bandCode} · ${day}`, header, rows, brk });
        });
      });
    } else if (view === 'class') {
      const list = scope === 'all' ? classes.map(c => c.classCode) : [scope];
      list.forEach(code => {
        const periods = slotsFor(stageOfClass(code));
        if (!periods.filter(p => !isStrip(p)).length) return;
        const bandCode = bandForStage(stageOfClass(code))?.bandCode;
        const brk = [false, ...periods.map(isStrip)];
        const header = ['Day', ...periods.map(p => isStrip(p) ? breakLabel(p) : periodHead(p))];
        const rows = days.map(day => [day, ...periods.map(p => isStrip(p) ? '' : cellForClass(day, p, code, bandCode))]);
        rows.push(nonformalRow(header.length));
        const label = pathway ? `${className(code)} — ${pathway}` : className(code);
        sheets.push({ name: (label || code).slice(0, 31), title: `${label} · ${year} Term ${term}`, header, rows, brk });
      });
    } else {
      const list = scope === 'all' ? teachers.map(t => t.employeeNo) : [scope];
      list.forEach(no => {
        // Teacher timetables are sparse: a period column is "used" only if some day
        // carries a lesson for this teacher. Empty columns are squeezed (compact
        // header, narrow width) so the sheet fits portrait — matching the print view.
        const used = mergedPeriods.map(p => days.some(day =>
          entries.find(x => x.employeeNo === no && x.day === day && x.startTime === p.startTime && x.type === 'lesson')));
        const header = ['Day', ...mergedPeriods.map((p, i) => used[i] ? periodHead(p) : (p.startTime || ''))];
        const empty = [false, ...used.map(u => !u)];
        const rows = days.map(day => [day, ...mergedPeriods.map(p => {
          const e = entries.find(x => x.employeeNo === no && x.day === day && x.startTime === p.startTime && x.type === 'lesson');
          return e ? cellFor(e.areaCode, (e.classCodes || []).length ? e.classCodes.join(',') : `${e.cohortCode || ''} elective`) : '';
        })]);
        sheets.push({ name: (teacherName(no) || no).slice(0, 31), title: `${teacherName(no)} · ${year} Term ${term}`, header, rows, brk: header.map(() => false), empty });
      });
    }
    return sheets;
  };

  const fileBase = () => {
    const pw = view === 'class' && pathway ? `-${pathway.replace(/[^a-z0-9]+/gi, '')}` : '';
    return `timetable-${view}${pw}-${year}-T${term}`;
  };

  const exportExcel = () => {
    const sheets = buildSheets();
    if (!sheets.length) { setError('Nothing to export yet — generate the timetable first.'); return; }
    const org = company.name || school.name || 'School';
    const wb = XLSX.utils.book_new();
    const used = new Set();
    sheets.forEach(s => {
      const ncol = s.header.length;
      const aoa = [[`${org} — ${s.title}`], s.header, ...s.rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // Squeezed (empty) teacher columns get an extra-narrow width, matching print.
      ws['!cols'] = s.header.map((_, i) => ({ wch: i === 0 ? 14 : ((s.empty && s.empty[i]) ? 4 : (s.brk[i] ? 6 : 12)) }));
      // Narrow print margins help the wide grid fit landscape. Setting !margins also
      // makes SheetJS emit <pageMargins>, which we anchor the <pageSetup> onto below.
      ws['!margins'] = { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: ncol - 1 } }];   // title spans all columns
      s.rows.forEach((r, ri) => { if (r[0] === 'Non-formal') ws['!merges'].push({ s: { r: ri + 2, c: 1 }, e: { r: ri + 2, c: ncol - 1 } }); });
      let nm = s.name || 'Sheet', n = nm, i = 2;
      while (used.has(n.toLowerCase())) { n = `${nm.slice(0, 28)} ${i++}`; }
      used.add(n.toLowerCase());
      XLSX.utils.book_append_sheet(wb, ws, n);
    });

    // SheetJS (community) does not write page orientation, so build the .xlsx, then
    // inject <pageSetup orientation="landscape" .../> into every worksheet XML (it
    // must follow <pageMargins/> per the schema) and re-zip. Result: the workbook
    // opens set to print landscape, fit to one page wide.
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const zip = unzipSync(new Uint8Array(buf));
    const PAGE_SETUP = '<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0" horizontalDpi="0" verticalDpi="0"/>';
    Object.keys(zip).forEach(name => {
      if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(name)) return;
      let xml = strFromU8(zip[name]);
      // Fit-to-page flag lives on <sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>,
      // which must be the first child of <worksheet>.
      if (!/<sheetPr[ >]/.test(xml)) {
        xml = xml.replace(/(<worksheet[^>]*>)/, '$1<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>');
      }
      if (/<pageMargins[^>]*\/>/.test(xml)) xml = xml.replace(/(<pageMargins[^>]*\/>)/, `$1${PAGE_SETUP}`);
      else xml = xml.replace('</worksheet>', `${PAGE_SETUP}</worksheet>`);
      zip[name] = strToU8(xml);
    });
    const out = zipSync(zip);
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${fileBase()}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const exportWord = () => {
    const sheets = buildSheets();
    if (!sheets.length) { setError('Nothing to export yet — generate the timetable first.'); return; }
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
    const org = company.name || school.name || 'School';
    const addr = [company.address, company.city, company.county && `${company.county} County`].filter(Boolean).join(', ');
    const tel = [company.phone && `Tel: ${company.phone}${company.phone2 ? ` / ${company.phone2}` : ''}`, company.email].filter(Boolean).join('   ·   ');
    // Break strips and squeezed empty columns (teacher view) render narrow, matching
    // the print grid so the Word document keeps the same compact layout.
    const brkTd = (i, s) => s.brk[i] ? 'background:#ececec;width:26px;text-align:center;' : '';
    const emptyAt = (i, s) => s.empty && s.empty[i];
    const emptyStyle = 'width:20px;padding:1px;font-size:8px;font-weight:normal;color:#999;';
    const tables = sheets.map((s, si) => {
      const head = `<tr>${s.header.map((h, i) => `<th style="background:#f0f0f0;${s.brk[i] ? 'width:26px;' : ''}${emptyAt(i, s) ? emptyStyle : ''}">${esc(h)}</th>`).join('')}</tr>`;
      const body = s.rows.map(r => {
        if (r[0] === 'Non-formal') return `<tr><td style="background:#f0f0f0;font-weight:bold">${esc(r[0])}</td><td colspan="${s.header.length - 1}" style="background:#f0f0f0;text-align:left">${esc(r[1])}</td></tr>`;
        return `<tr>${r.map((c, i) => `<td style="${i === 0 ? 'font-weight:bold;background:#fafafa;' : ''}${brkTd(i, s)}${emptyAt(i, s) ? 'width:20px;padding:1px;' : ''}">${esc(c)}</td>`).join('')}</tr>`;
      }).join('');
      return `${si ? '<br clear="all" style="page-break-before:always"/>' : ''}<h3 style="font-family:Arial;margin:14px 0 4px">${esc(s.title)}</h3><table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;font-family:Arial;font-size:11px"><thead>${head}</thead><tbody>${body}</tbody></table>`;
    }).join('');
    const header = `<h2 style="font-family:Arial;margin:0">${esc(org)}</h2>${addr ? `<div style="font-family:Arial;font-size:11px">${esc(addr)}</div>` : ''}${tel ? `<div style="font-family:Arial;font-size:11px">${esc(tel)}</div>` : ''}<div style="font-family:Arial;font-size:12px;margin:6px 0 10px"><b>Timetable</b> · ${esc(year)} Term ${esc(term)}</div>`;
    const footer = `<div style="font-family:Arial;font-size:10px;color:#777;text-align:center;margin-top:14px;border-top:1px solid #ccc;padding-top:6px">EduMIS · Powered by Dataport Technologies</div>`;
    // All timetables print landscape so wide grids fit easily. Word reads the
    // orientation from the @page section.
    const pageCss = `<style>@page Section1 { size: 29.7cm 21.0cm; mso-page-orientation: landscape; margin: 1cm; } div.Section1 { page: Section1; }</style>`;
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>Timetable</title>${pageCss}</head><body><div class="Section1">${header}${tables}${footer}</div></body></html>`;
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${fileBase()}.doc`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const scopeOptions = view === 'class'
    ? classes.map(c => ({ value: c.classCode, label: c.className || c.classCode }))
    : view === 'teacher'
      ? teachers.map(t => ({ value: t.employeeNo, label: `${t.firstName} ${t.lastName}` }))
      : [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: { xs: 1, sm: 0 } }}>
      <style>{printCss()}</style>

      <TimetableNav className="no-print" />

      <Card elevation={3} className="no-print" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Print color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Timetable Views &amp; Print</Typography>
                <Typography variant="body2" color="text.secondary">Per-class, per-teacher and master timetables</Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics"><ArrowBack /></IconButton>
              <IconButton onClick={() => navigate('/dashboard')} title="Home"><Home /></IconButton>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
            <TextField select label="Academic Year" value={year} size="small" sx={{ minWidth: 150 }}
              onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={term} size="small" sx={{ minWidth: 110 }}
              onChange={e => setTerm(parseInt(e.target.value, 10))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
              <ToggleButton value="class">Per Class</ToggleButton>
              <ToggleButton value="teacher">Per Teacher</ToggleButton>
              <ToggleButton value="master">Master</ToggleButton>
            </ToggleButtonGroup>
            {view !== 'master' && (
              <TextField select label={view === 'class' ? 'Class' : 'Teacher'} value={scope} size="small" sx={{ minWidth: 200 }}
                onChange={e => setScope(e.target.value)}>
                <MenuItem value="all">— All ({view === 'class' ? classes.length : teachers.length}) —</MenuItem>
                {scopeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
            {view === 'class' && (
              <TextField select label="Pathway" value={pathway} size="small" sx={{ minWidth: 190 }}
                helperText="Splits option blocks; commons carry"
                onChange={e => setPathway(e.target.value)}>
                <MenuItem value="">Whole class (all options)</MenuItem>
                {LEARNER_PATHWAYS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            )}
            <Button variant="outlined" color="success" startIcon={<GridOn />} onClick={exportExcel}>Excel</Button>
            <Button variant="outlined" startIcon={<Description />} onClick={exportWord}>Word</Button>
            <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" className="no-print" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box textAlign="center" py={5} className="no-print"><CircularProgress /></Box>
      ) : !config ? (
        <Alert severity="warning" className="no-print">No timetable setup for {year || '—'} Term {term}. Seed it under Timetable Setup first.</Alert>
      ) : entries.length === 0 ? (
        <Alert severity="info" className="no-print">No lessons placed yet for {year} Term {term}. Build the grid first.</Alert>
      ) : (
        <Box className="tt-print-area">{renderSheets()}</Box>
      )}
    </Box>
  );
};

export default TimetablePrint;
